import { useCallback, useEffect, useMemo, useState } from "react";
import { Compass, MapPin, Plus, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AIParseDialog } from "@/components/AIParseDialog";
import { UpgradeModal } from "@/components/UpgradeModal";
import { SemanticFeatureChips } from "@/components/SemanticFeatureChips";
import { StatusChip } from "@/components/StatusChip";
import { DOMBadge } from "@/components/DOMBadge";
import { SkeletonGrid } from "@/components/Skeleton";
import { formatDateShort, formatPhp } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function formatBudget(budget, txnType) {
  const formatted = formatPhp(budget, { hideZero: true });
  if (formatted === "—") return "any budget";
  return formatted;
}

export default function RequirementsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const trialExpired = user?.trial?.expired && user?.trial?.plan !== "pro";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/requirements/mine");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load requirements"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (created) => {
    setItems((prev) => [created, ...prev]);
  };

  const handleClose = async (id) => {
    try {
      const { data } = await api.post(`/requirements/${id}/close`);
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Requirement closed.");
    } catch (e) {
      toast.error(formatApiError(e, "Close failed"));
    }
  };

  const activeCount = useMemo(
    () => items.filter((r) => r.status === "Active").length,
    [items]
  );

  return (
    <section
      className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10 md:py-16 mb-rise"
      data-testid="page-requirements"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
            Requirements
          </div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Buyer requirements
          </h1>
          <p
            className="mt-3 max-w-xl text-base leading-relaxed text-gray-500"
            data-testid="requirements-count"
          >
            {loading
              ? "Loading…"
              : `${activeCount} active · ${items.length} total`}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => (trialExpired ? setShowUpgrade(true) : setDialogOpen(true))}
          className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A]"
          data-testid="add-requirement-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add requirement
        </Button>
      </div>

      {(() => {
        if (loading) {
          return (
            <div className="mt-10" data-testid="requirements-loading-skeleton">
              <SkeletonGrid count={6} />
            </div>
          );
        }
        if (items.length === 0) {
          return (
            <div
              className="mt-10 rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-mb-card"
              data-testid="requirements-empty-state"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1B2B4B]/10">
                <Sparkles className="h-6 w-6 text-[#1B2B4B]" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-gray-900">
                No requirements yet.
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
                Paste a buyer requirement — the AI will structure it and publish it
                to the network.
              </p>
              <Button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="mb-focus-ring mt-6 h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A]"
                data-testid="requirements-empty-add-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add your first requirement
              </Button>
            </div>
          );
        }
        return (
        <ul
          className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8"
          data-testid="requirements-grid"
        >
          {items.map((r) => (
            <li
              key={r.id}
              className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-mb-card transition hover:-translate-y-[1px] hover:shadow-mb-card-hover"
              data-testid={`requirement-card-${r.id}`}
            >
              <div className="flex items-start gap-2">
                <Compass className="mt-0.5 h-5 w-5 flex-none text-gray-400" />
                <div className="min-w-0 flex-1">
                  <h3
                    className="truncate text-lg font-semibold text-gray-900"
                    data-testid={`requirement-location-${r.id}`}
                  >
                    {r.target_location}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {r.property_category || r.property_type}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-0.5 font-semibold uppercase tracking-wider text-gray-600"
                  data-testid={`requirement-transaction-${r.id}`}
                >
                  {r.transaction_type || "Wanted to Buy"}
                </span>
                {r.bedrooms > 0 && (
                  <span className="text-gray-500">{r.bedrooms} BR min</span>
                )}
                {r.floor_area_sqm != null && r.floor_area_sqm > 0 && (
                  <span className="text-gray-500">
                    · {r.floor_area_sqm} sqm FA min
                  </span>
                )}
                {r.lot_area_sqm != null && r.lot_area_sqm > 0 && (
                  <span className="text-gray-500">
                    · {r.lot_area_sqm} sqm lot min
                  </span>
                )}
              </div>

              <div className="text-2xl font-bold tracking-tight text-gray-900">
                Up to{" "}
                <span data-testid={`requirement-budget-${r.id}`}>
                  {formatBudget(r.budget_max ?? r.max_budget, r.transaction_type)}
                </span>
                {(r.transaction_type || "").includes("Lease") && (
                  <span className="ml-1 text-sm font-medium text-gray-500">
                    /month
                  </span>
                )}
              </div>

              {r.notes && (
                <p
                  className="line-clamp-3 text-sm leading-relaxed text-gray-500"
                  data-testid={`requirement-notes-${r.id}`}
                >
                  {r.notes}
                </p>
              )}

              {r.preferred_features && r.preferred_features.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {r.preferred_features.slice(0, 6).map((f, idx) => (
                    <span
                      key={`${f}-${idx}`}
                      className="rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              )}

              {r.semantic_features && (
                <SemanticFeatureChips
                  value={r.semantic_features}
                  limit={5}
                  variant="navy"
                  testId={`requirement-semantic-features-${r.id}`}
                  keyPrefix={`${r.id}-sf`}
                />
              )}

              <div className="mt-auto flex items-center gap-2 border-t border-gray-100 pt-4">
                <StatusChip status={r.status} />
                <DOMBadge
                  createdAt={r.created_at}
                  testId={`requirement-dom-${r.id}`}
                />
                <span className="ml-auto text-xs text-gray-400">
                  {formatDateShort(r.created_at)}
                </span>
              </div>

              {r.status === "Active" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(r.id)}
                  className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  data-testid={`close-requirement-button-${r.id}`}
                >
                  <X className="mr-2 h-3.5 w-3.5" />
                  Close requirement
                </Button>
              )}
            </li>
          ))}
        </ul>
        );
      })()}

      <AIParseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode="requirement"
        onSaved={handleSaved}
        onUpgradeRequired={() => setShowUpgrade(true)}
      />
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </section>
  );
}
